import { memo, useRef } from 'react';

import { Button } from '@/shadcn/button';
import { Input } from '@/shadcn/input';

import type { WorkflowGroupsQueryQuery } from '@/apollo/graphql';
import { useKeyboardShortcut } from '@/hooks/useKeyboardShortcut';
import { TestId } from '@/types/testId';
import { pluralize } from '@/utils';

type Props = {
  workflow: WorkflowGroupsQueryQuery['workflowByRowId'];
  searchGroups: string;
  updateSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  openAddGroupModal: () => void;
  isLoading: boolean;
};

export const SubHeader = memo(
  ({
    workflow,
    searchGroups,
    updateSearch,
    openAddGroupModal,
    isLoading
  }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);

    useKeyboardShortcut(inputRef, {
      key: 'f',
      ctrlKey: true,
      preventDefault: true
    });

    const {
      nonFilteredGroups: { totalCount: nbNonFilteredGroups } = {},
      groups: { totalCount: nbFilteredGroups } = {}
    } = workflow || {};

    let groupsCount = 'No groups added yet';
    if (nbNonFilteredGroups) {
      const totalGroupsCount = pluralize(
        nbNonFilteredGroups,
        'group',
        false,
        true
      );
      if (searchGroups) {
        groupsCount = `${nbFilteredGroups} of ${totalGroupsCount}`;
      } else {
        groupsCount = totalGroupsCount;
      }
    }

    return (
      <div className="flex shrink-0 flex-row flex-nowrap items-center justify-between border-border border-b bg-card px-5 py-4">
        <div className="flex flex-col flex-nowrap items-start sm:flex-row sm:items-center">
          <div className="mr-5 font-semibold text-2xl">Groups settings</div>
          <div className="mt-2.5 sm:mt-0">
            <div className="*:not-first:mt-2">
              <div className="relative">
                <Input
                  aria-label="Search groups"
                  autoFocus
                  className="h-8 bg-muted pe-11"
                  data-testid={TestId.WorkflowGroups.SubHeader.SearchInput}
                  disabled={isLoading}
                  onChange={updateSearch}
                  placeholder="Search groups..."
                  ref={inputRef}
                  type="search"
                  value={searchGroups}
                />
                <div className="pointer-events-none absolute inset-y-0 end-0 flex items-center justify-center pe-2 text-muted-foreground">
                  <kbd className="inline-flex h-5 max-h-full items-center rounded border border-input bg-muted px-1 font-[inherit] font-medium text-[0.625rem] text-muted-foreground/70">
                    ⌘F
                  </kbd>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row flex-nowrap items-center justify-end gap-3">
          {workflow && (
            <div className="font-medium text-muted-foreground text-xs">
              {groupsCount}
            </div>
          )}
          <Button
            data-testid={TestId.WorkflowGroups.SubHeader.AddGroupButton}
            onClick={openAddGroupModal}
          >
            Add a new group
          </Button>
        </div>
      </div>
    );
  }
);
